import React from "react";
import { ApiContext } from "../common/context/ApiContext";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useTranslation } from "react-i18next";
import { Row } from "react-bootstrap";
import { BackNav, FlexRow, PageContainer } from "../common/CommonStyles";
import { Link } from "react-router-dom";
import { SchoolInsightReportInfo } from "../common/api";
import Moment from "moment";

// period_start / period_end / data_through are civil dates ("2026-06-30") with no time and no
// zone. Moment parses that shape as local midnight, avoiding the off-by-one a bare
// `new Date("2026-06-30")` (parsed as UTC) gives west of UTC.
const formatDate = (value: string | null): string =>
  value ? Moment(value).format("MMM D, YYYY") : "—";

// generated_at is a real instant, not a civil date, so it converts to the viewer's zone and keeps
// the time. Formatting it as a civil date would drop both — and the nightly job runs late enough
// in Eastern that the UTC day is often already the next one.
const formatDateTime = (value: string | null): string =>
  value ? Moment(value).format("MMM D, YYYY h:mm a") : "—";

export const SchoolInsightReportListPage: React.FC = () => {
  const { t } = useTranslation();
  const { getSchoolInsightReports } = React.useContext(ApiContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const { setCurrentTab } = React.useContext(TabsContext);
  const [account, setAccount] = React.useState<Account>();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const listFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);
  const [reports, setReports] = React.useState<SchoolInsightReportInfo[] | null>(
    null
  );

  React.useEffect(() => {
    setCurrentTab(TabEnum.SchoolInsights);
  }, [setCurrentTab]);

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    (async () => {
      if (!account) {
        return;
      }
      if (
        account.userCurrentVersion !== listFetchedForUserVersion.current &&
        account.userCurrentVersion !== fetchJobForUserVersion.current
      ) {
        startLoading();
        const expectedVersion = (fetchJobForUserVersion.current =
          account.userCurrentVersion);
        const response = await getSchoolInsightReports();
        if (expectedVersion !== fetchJobForUserVersion.current) {
          return;
        }
        setReports(response.reports);
        listFetchedForUserVersion.current = fetchJobForUserVersion.current;
        stopLoading();
      }
    })();
  }, [account, getSchoolInsightReports, startLoading, stopLoading]);

  if (!reports) {
    return <></>;
  }

  return (
    <PageContainer>
      <Row className="align-items-center">
        <FlexRow>
          <BackNav label={t("generic_nav_back")} />
        </FlexRow>
      </Row>
      <div className="text-center mt-4 mb-3">
        <h3>{t("school_insights_title")}</h3>
      </div>
      {reports.length === 0 ? (
        <p className="text-center text-muted">{t("school_insights_empty")}</p>
      ) : (
        <div>
          <table className="table align-middle">
            <thead>
              <tr>
                <th scope="col">{t("school_insights_school")}</th>
                <th scope="col">{t("school_insights_district")}</th>
                <th scope="col">{t("school_insights_period")}</th>
                <th scope="col">{t("school_insights_covers")}</th>
                <th scope="col">{t("school_insights_generated")}</th>
                <th scope="col" className="text-end">
                  {t("school_insights_report")}
                </th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>
                    <Link
                      className="text-decoration-none"
                      to={`/schools/dashboard/?school_id=${report.school_id}`}
                    >
                      {report.school_name}
                    </Link>
                  </td>
                  <td>{report.district_name}</td>
                  <td>
                    {report.period_label}
                    {report.period_description && (
                      <div className="text-muted small">
                        {report.period_description}
                      </div>
                    )}
                  </td>
                  <td>
                    {formatDate(report.period_start)} –{" "}
                    {formatDate(report.data_through)}
                    {report.is_partial && (
                      <div className="text-muted small">
                        {t("school_insights_in_progress")}
                      </div>
                    )}
                    {report.is_frozen && (
                      <div className="text-muted small">
                        {t("school_insights_final")}
                      </div>
                    )}
                  </td>
                  <td>{formatDateTime(report.generated_at)}</td>
                  <td className="text-end">
                    {report.html_url ? (
                      // A plain link, not a router route: the report is a stored HTML document
                      // served by the API, and the JWT cookie set at login authenticates the
                      // navigation.
                      <a
                        href={report.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("school_insights_open")}
                      </a>
                    ) : (
                      <span
                        className="text-danger"
                        title={report.error || undefined}
                      >
                        {t("school_insights_failed")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
};
